import { ContentBlock } from '@tytapp/content';
import { ContentBlockType } from '@tytapp/content';
import { ContentBlockEditorComponent } from '@tytapp/content/content-block-editor-component';
import { Component, HostBinding } from '@angular/core';
import { ContentBlockViewComponent } from '@tytapp/content/content-block-view-component';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Icon } from '@tytapp/common-ui';

export interface Button {
    text: string;
    icon?: Icon;
    style?: string;
    url: string;
    stretch?: boolean;
}

export interface C2AButtonBlock extends ContentBlock {
    alignment?: 'left' | 'center' | 'right';
    buttons: Button[];
}

@Component({
    template: `
        <a
            *ngFor="let button of block.buttons"
            mat-button

            [class.mat-raised-button]="button.style !== 'discrete'"
            [color]="
                button.style === 'primary'
                    ? 'primary' :
                button.style === 'secondary'
                    ? 'secondary' :
                undefined
            "
            [url]="button.url"
            [attr.data-style]="button.style"
            [class.stretch]="button.stretch"
            >
            <tyt-icon *ngIf="button.icon && button.icon.type !== 'none'" size="24" [icon]="button.icon"></tyt-icon>
            {{ button.text ?? 'Button' }}
        </a>
    `,
    styles: [`
        @use '../styles/colors' as colors;

        :host {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 1em;
            margin: 1em 0;
        }
        :host[data-alignment="center"] { justify-content: center; }
        :host[data-alignment="right"] { justify-content: right; }

        a {
            $c2a-padding: 0.25em 2em;

            &.stretch {
                flex-grow: 1;
            }

            &[data-style="membership"] {
                color: black;
                background-color: colors.$tyt-plus;
                border-radius: 0;
                padding: $c2a-padding;
            }
            &[data-style="donation"] {
                color: white;
                background-color: colors.$tyt-donation;
                border-radius: 0;
                padding: $c2a-padding;
            }
            &[data-style="community"] {
                color: black;
                background-color: colors.$tyt-community;
                border-radius: 0;
                padding: $c2a-padding;
            }
            &[data-style="wasabi"] {
                color: black;
                background-color: #BCCC6D;
            }
            &[data-style="strawberry"] {
                color: black;
                background-color: #C73F4A;
            }
        }
    `]
})
export class ViewComponent extends ContentBlockViewComponent<C2AButtonBlock> {
    @HostBinding('attr.data-alignment') get alignment() { return this.block.alignment; }
}

@Component({
    template: `
        <div class="button-list" cdkDropList (cdkDropListDropped)="drop(block.buttons, $event)">
            <div class="button" *ngFor="let button of block.buttons" cdkDrag>
                <div class="move-placeholder" *cdkDragPlaceholder></div>
                <mat-menu #menu>
                    <button mat-menu-item (click)="removeButton(button)">
                        <mat-icon>close</mat-icon>
                        Remove
                    </button>
                </mat-menu>

                <div class="handle-and-icon">
                    <mat-icon cdkDragHandle>drag_handle</mat-icon>
                    <tyt-icon-editor [(icon)]="button.icon"></tyt-icon-editor>
                </div>
                <div class="spacer">
                    <div class="field-row">
                        <mat-form-field class="text" appearance="outline" floatLabel="always">
                            <mat-label>Text</mat-label>
                            <input type="text" matInput [(ngModel)]="button.text" />
                        </mat-form-field>
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>Style</mat-label>
                            <mat-select [(ngModel)]="button.style">
                                <mat-option value="primary">Primary</mat-option>
                                <mat-option value="default">Alternative</mat-option>
                                <mat-option value="discrete">Discrete</mat-option>
                                <mat-option value="membership">Membership</mat-option>
                                <mat-option value="donation">Donation</mat-option>
                                <mat-option value="community">Community</mat-option>
                                <mat-option value="wasabi">Wasabi</mat-option>
                                <mat-option value="strawberry">Strawberry</mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>
                    <div class="field-row">
                        <mat-form-field class="url" appearance="outline" floatLabel="always">
                            <mat-label>URL</mat-label>
                            <input type="text" matInput [(ngModel)]="button.url" />
                        </mat-form-field>
                        <mat-checkbox [(ngModel)]="button.stretch">Stretch</mat-checkbox>
                        <button mat-icon-button [matMenuTriggerFor]="menu">
                            <mat-icon>more_vert</mat-icon>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="controls">
            <button mat-raised-button (click)="addButton()">
                <mat-icon>add</mat-icon>
                Add another
            </button>
            <div class="spacer"></div>
            <mat-button-toggle-group [(ngModel)]="block.alignment">
                <mat-button-toggle value="left" aria-label="Text align left">
                    <mat-icon>format_align_left</mat-icon>
                </mat-button-toggle>
                <mat-button-toggle value="center" aria-label="Text align center">
                    <mat-icon>format_align_center</mat-icon>
                </mat-button-toggle>
                <mat-button-toggle value="right" aria-label="Text align right">
                    <mat-icon>format_align_right</mat-icon>
                </mat-button-toggle>
            </mat-button-toggle-group>
        </div>

        <h2>Preview</h2>

        <div class="preview">
            <tyt-content-block [block]="block"></tyt-content-block>
        </div>
    `,
    styles: [
        `
        :host {
            display: flex;
            flex-direction: column;
            gap: 1em;
        }

        mat-form-field.columns {
            width: 5em;
        }

        .preview {
            border: 1px solid #454f78;
            border-radius: 5px;
            margin: 1em;
            padding: 1em;
        }

        mat-form-field.url {
            flex-grow: 1;
        }

        .handle-and-icon {
            display: flex;
            align-items: center;
            gap: 1em;
            margin: 12px 0;
        }

        .text {
            flex-grow: 1;
        }

        .controls {
            display: flex;
            align-items: baseline;
            gap: 1em;
        }

        .button {
            display: flex;
            gap: 1em;
            align-items: flex-start;
        }

        :host[data-alignment="center"] ::ng-deep {
            tyt-markdown-field textarea {
                text-align: center;
            }
        }

        :host[data-alignment="right"] ::ng-deep {
            .heading mat-form-field input[type="text"] {
                text-align: right;
            }

            tyt-markdown-field textarea {
                text-align: right;
            }
        }

        ::ng-deep .move-placeholder {
            width: 100%;
            height: 2em;
            border: 4px dashed white;
        }

        .cdk-drag-preview {
            opacity: 0.8;
            backdrop-filter: opacity(0);
            background-color: var(--content-background);
            color: var(--content-foreground);
        }

        `
    ]
})
export class EditorComponent extends ContentBlockEditorComponent<C2AButtonBlock> {
    @HostBinding('attr.data-alignment')
    get alignment() { return this.block.alignment; }

    addButton() {
        this.block.buttons ??= [];
        this.block.buttons.push({
            text: '',
            url: '',
            style: this.block.buttons.length === 0 ? 'primary' : 'default'
        })
    }

    removeButton(button: Button) {
        let index = this.block.buttons.indexOf(button);
        if (index >= 0)
            this.block.buttons.splice(index, 1);
    }

    drop(array, event: CdkDragDrop<Button[]>) {
        moveItemInArray(array, event.previousIndex, event.currentIndex);
    }
}


export const CALL_TO_ACTION_BLOCK_TYPE: ContentBlockType<C2AButtonBlock> = {
    id: 'button',
    label: 'Button(s)',
    icon: 'smart_button',
    template: {
        alignment: 'center',
        buttons: [
            {
                text: '',
                url: '/'
            }
        ]
    },
    preview: {
        alignment: 'center',
        buttons: [
            {
                text: 'Enter Text Here',
                style: 'primary',
                url: 'https://google.com'
            }
        ]
    },
    previewSettings: {
        horizontalAlign: 'center',
        verticalAlign: 'center'
    },
    editorComponent: EditorComponent,
    viewComponent: ViewComponent
}